import Passenger from '@/components/Passenger/models/Passenger';
import { IPassengerWithSms } from '@/components/Passenger/models/PassengerWithSms';
import RouteWithTrips, { CarLocation } from '@/components/Passenger/models/RouteWithTrips';
import TransportationPermission from '@/components/Passenger/models/TransportationPermission';
import Trip from "@/components/Passenger/models/Trip";
import { IRideitChangeRequest } from '@/models/Rideit/RideitChangeRequest';
import { IRideitInvitation } from '@/models/Rideit/RideitInvitation';
import { IRideitNotice, IRideitNoticeCreateRequest } from '@/models/Rideit/RideitNotice';
import { IRideitPassengerToken } from '@/models/Rideit/RideitPassengerToken';
import { IEntityMatchResult, IPassengerMatchRequest, ITransportationPermissionMatchRequest } from '@/typings/import';
import { IRideitCreateInvitationsRequest } from '@/typings/rideit';
import { 
  IMovitInstance,
  IPaginatedPassengerRequest,
  IPaginatedPassengerResponse,
  IPaginationResult,
  IPassengerGroup,
  IPassengerSearchResult,
  ISchedule,
  ITimetableSearchResult } from "@/typings/timit";

import axios, { AxiosResponse } from "axios";

const baseURL = process.env.VUE_APP_API_HOST;

export default {
  passengers: {
    async cancelPassengerTrips(id: number, trips: any) {
      const url = baseURL + `/api/passenger/${id}/trips/cancel`
      const result = await axios.post(url, trips);
      return result.data as any;
    },
    async createPassenger(passenger: Passenger) {
      const url = baseURL + `/api/passenger`;
      const result = await axios.post(url, passenger);
      return result.data as Passenger;
    },
    async getPaginatedPassengers(request: IPaginatedPassengerRequest) {
      const url = baseURL + `/api/passenger/paginated`;
      const result = await axios.get(url, {
        params: request,
        
      });
      return result.data as IPaginatedPassengerResponse;
    },
    async getPaginatedPassengersWithSms(request: IPaginatedPassengerRequest) {
      const url = baseURL + `/api/passenger/paginated/sms`;
      const result = await axios.get(url, {
        params: request,
      });
      return result.data as IPaginationResult<IPassengerWithSms>;
    },
    async getPassengerPassengerGroups(id: number) {
      const url = baseURL + `/api/passenger/${id}/passengerGroups`;
      const result = await axios.get(url);
      return result.data as IPassengerGroup[];
    },
    async getPassengerAvailableDestinationGroups(id: number): Promise<string[]> {
      const url = baseURL + `/api/passenger/${id}/availableDestinationGroups`;
      const result = await axios.get(url);
      return result.data as string[];
    },
    async getPassengersByIds(ids: number[]): Promise<Passenger[]> {
      const url = baseURL + `/api/passenger/byIds`;
      const result = await axios.post(url, ids);
      return result.data.map((x: unknown) => new Passenger(x));
    },
    async getPassengerTrips(id: number, startDate: string, endDate: string, destinationId: number) {
      const url = baseURL + `/api/passenger/${id}/trips?startDate=${startDate}&endDate=${endDate}&destinationId=${destinationId}`
      const result = await axios.get(url);
      return result.data as Trip[];
    },
    async getPassengerRouteWithTrips(id: number, tripId: number, tripDate: string) {
      const url = baseURL + `/api/passenger/${id}/routeWithTrips?tripId=${tripId}&tripDate=${tripDate}`;
      const result = await axios.get(url);
      return result.data as RouteWithTrips;
    },
    async getCarLocationByTripId(id: number, tripId: number, tripDate: string) {
      const url = baseURL + `/api/passenger/${id}/location?tripId=${tripId}&tripDate=${tripDate}`;
      const result = await axios.get(url);
      return result.data as CarLocation;
    },
    async getSsn(id: number) {
      const url = baseURL + `/api/passenger/${id}/ssn`
      const result = await axios.get(url);
      return result.data as string;
    },
    async matchPassengers(request: IPassengerMatchRequest) {
      const searchParams = `?externalId=${request.externalId}&ssn=${request.ssn}&name=${request.name}`;
      const url = baseURL + `/api/passenger/importer/search${searchParams}`;
      const result = await axios.get(url);
      return result.data as Array<IEntityMatchResult<Passenger>>;
    },
    async searchPassengers(search: string, searchParams: any) {
      let url = baseURL + `/api/passenger/search?q=${search}`;
      if (searchParams){
        const searchParamKeys = Object.keys(searchParams);
        if (searchParamKeys.length){
          for(const key of searchParamKeys){
            url = `${url}&${key}=${searchParams[key]}`;
          }
        }
      }
      
      const result = await axios.get(url);
      return result.data as IPassengerSearchResult[];
    },
    async setPassengerSsn(id: number, ssn: string) {
      const url = baseURL + `/api/passenger/${id}/ssn`;
      const result = await axios.post(url, {Ssn: ssn});
      return result.data as string;
    },
    async updatePassenger(passenger: Passenger) {
      const url = baseURL + `/api/passenger/${passenger.id}`;
      const result = await axios.put(url, passenger);
      return result.data as Passenger;
    }
  },

  trip: {
    async getTripEstimates(tripId: number) {
      const url = baseURL + `/api/trip/${tripId}/estimates`;
      const result = await axios.get(url);
      return result.data as any;
    }
  },
  
  organizations: {
    async getTransportationPermissions(id: string): Promise<TransportationPermission[]> {
      const url = baseURL + `/api/organization/${id}/passengerTransportationPermissions`;
      const result = await axios.get(url);
      return result.data as TransportationPermission[];
    },
    async getServiceProviders(id: string) {
      const url = baseURL + `/api/organization/${id}/instances`;
      const result = await axios.get(url);
      return result.data as IMovitInstance[];
    },
    async getPassengersGroups(organizationId: string) {
      const url = baseURL + `/api/organization/${organizationId}/passengers/groups`;
      const result = await axios.get(url);
      return result.data as string[];
    },
  },
  rideit: {
    async cancelToken(tokenId: number, organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/tokens/${tokenId}`;
      const result = await axios.delete(url);
      return result.data as IRideitPassengerToken;
    },
    async createInvitations(request: IRideitCreateInvitationsRequest, organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/invitations`;
      const result = await axios.post(url, request);
      return result.data as boolean;
    },
    async createNotice(notice: IRideitNoticeCreateRequest, organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/notices` 
      const result = await axios.post(url, notice);
      return result.data as IRideitNotice;
    },
    async deleteNotice(noticeId: string, organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/notices/${noticeId}`;
      const result = await axios.delete(url);
      return result.data as IRideitNotice;
    },
    async getInvitations(organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/invitations`;
      const result = await axios.get(url);
      return result.data as IRideitInvitation[];
    },
    async getNotices(organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/notices`;
      const result = await axios.get(url);
      return result.data as IRideitNotice[];
    },
    async getPassengerTokens(organizationId: string, active: boolean = false) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/tokens?active=${active}`;
      const result = await axios.get(url);
      return result.data as IRideitPassengerToken[];
    },
    async getTokensForPassenger(passengerId: number) {
      const url = baseURL + `/api/rideit/passengers/${passengerId}/tokens`;
      const result = await axios.get(url);
      return result.data as IRideitPassengerToken[];
    },
    async updateNotice(organizationId: string, notice: IRideitNotice) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/notices`;
      const result = await axios.put(url, notice);
      return result.data as IRideitNotice;
    },
    async getChangeRequests(organizationId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/changeRequests`;
      const result = await axios.get(url);
      return result.data as IRideitChangeRequest[];
    },
    async approveChangeRequest(organizationId: string, changeRequestId: string, changeRequest: IRideitChangeRequest) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/changeRequests/${changeRequestId}/accept`;
      const result = await axios.post(url, changeRequest);
      return result.data as IRideitChangeRequest;
    },
    async rejectChangeRequest(organizationId: string, changeRequestId: string) {
      const url = baseURL + `/api/rideit/organizations/${organizationId}/changeRequests/${changeRequestId}/reject`;
      await axios.post(url);
    },
  },
  schedules: {
    async createSchedule(schedule: ISchedule) {
      const url = baseURL + `/api/schedule`;
      const result = await axios.post(url, schedule);
      return result.data as ISchedule;
    },
    async updateSchedule(schedule: ISchedule) {
      const url = baseURL + `/api/schedule`;
      const result = await axios.put(url, schedule);
      return result.data as ISchedule;
    },
    async getPassengerSchedules(passengerId: number) {
      const url = baseURL + `/api/schedule/passenger/${passengerId}`;
      const result = await axios.get(url);
      return result.data as ISchedule[];
    },
  },

  timeTables: {
    async searchTimeTable(search: string) {
      const url = baseURL + `/api/timeTable/search?q=${search}`;
      const result = await axios.get(url);
      return result.data as ITimetableSearchResult[];
    }
  },

  transportationPermissions: {
    async matchTransportationPermissions(request: ITransportationPermissionMatchRequest) {
      const searchParams = `?name=${request.permissionName}`;
      const url = baseURL 
        + `/api/passenger/importer/search/${request.passengerId}/transportationPermissions`
        + searchParams;
      const result = await axios.get(url);
      return result.data as Array<IEntityMatchResult<TransportationPermission>>;
    },
    async createTransportationPermission(ptp: TransportationPermission) {
      const url = baseURL + `/api/passenger/${ptp.passengerId}/transportationPermission`;
      const result = await axios.post(url, ptp);
      return result.data as TransportationPermission;
    },
  }
};

function serializePaginatedPassengerParams(params: IPaginatedPassengerRequest) {
  const parts: string[] = [];

  const add = (key: string, value: any) => {
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  };

  params.destinationIds?.forEach(v => add("destinationIds[]", v));
  params.excludeIds?.forEach(v => add("excludeIds[]", v));

  add("index", params.index);
  add("count", params.count);
  add("sortBy", params.sortBy);
  add("descending", params.descending);
  add("onlyActive", params.onlyActive);

  if (params.nameOrYearGradeSearch) {
    add("nameOrYearGradeSearch", params.nameOrYearGradeSearch);
  }

  return parts.join("&");
}
